import type { CarProfile } from '../cars/CarProfile';
import { ShiftLadder, type LadderState } from './ShiftLadder';

/**
 * VirtualDrivetrain — the heart of ROARDRIVE.
 *
 * The car only tells us how fast we go (and, if we are lucky, how hard the
 * pedal is pressed). This module invents the rest: a virtual gearbox, engine
 * rpm with inertia, throttle estimation, limiter bounce, lift-off detection,
 * shift torque-cuts and downshift blips. The AudioEngine only ever sees the
 * resulting DrivetrainState + events, so it is identical for mock, GPS or CAN.
 */

export type ShiftMode = 'auto' | 'manual';
/** Shifting character: eco short-shifts early; sport runs every gear to the limiter. */
export type DriveStyle = 'eco' | 'sport';
export type LoadState = 'idle' | 'accel' | 'cruise' | 'coast' | 'brake' | 'shift' | 'limiter';

export interface DrivetrainInput {
  speedKmh: number;
  /** m/s², null when the source cannot provide it (we derive it from speed). */
  accelMs2: number | null;
  /** 0..1 or null when unknown (we estimate it from acceleration). */
  pedal: number | null;
  /** 0..1 or null. */
  brake: number | null;
}

export type EngineState = 'off' | 'cranking' | 'on';

export interface DrivetrainState {
  /** ignition: off (rpm → 0), cranking (starter, ~280 rpm), on (normal running) */
  engine: EngineState;
  /** launch control armed (standing, engine held at launch rpm) */
  launch: boolean;
  /** pit speed limiter engaged (speed capped by the session) */
  pitLimiter: boolean;
  rpm: number;
  /** rpm for the DASH needle/number: the real value plus ±25 rpm of life (a needle never sits pinned) */
  rpmDisplay?: number;
  /** rpm normalised 0..1 between idle and maxRpm. */
  rpmNorm: number;
  /** 1-based gear. */
  gear: number;
  /** Effective throttle 0..1 (after shift cuts / blips). */
  throttle: number;
  /** Driver throttle intent 0..1 (before cuts). */
  throttleIntent: number;
  brake: number;
  load: LoadState;
  limiter: boolean;
  shifting: boolean;
  /** +1 upshift in progress, -1 downshift, 0 none. */
  shiftDir: -1 | 0 | 1;
  liftOff: boolean;
  /** Stationary and revving freely (clutch in / neutral). */
  freeRev: boolean;
  /** the car's selector is P / R / N (CAN): the dash shows N, the pedal revs the engine, the wheels do not drive it */
  neutral: boolean;
  /** CAN selector letter (P / R / N / D) or null */
  selector: string | null;
  /** Clutch open or slipping (standstill, pull-away, braked stop) — the wheels do not own the engine. */
  clutchSlip: boolean;
  speedKmh: number;
  accelMs2: number;
  /** top of the LAST gear — the driver's own gearing after scaling; the audio layers ride ratios of it */
  topSpeedKmh: number;
  /** the rpm the box will actually upshift at RIGHT NOW — the shift lights must fill toward this, not
   *  toward the redline: in DRIVE it is well below it, so a strip scaled to maxRpm never completes */
  upshiftRpm: number;
  /** the shift strip, sequenced HERE so the box can wait for it: LEDs lit per side, blue phase, blackout */
  shiftLights: LadderState;
  mode: ShiftMode;
}

export type DrivetrainEvent =
  | { type: 'shiftUp'; fromGear: number; toGear: number; rpm: number }
  | { type: 'shiftDown'; fromGear: number; toGear: number; rpm: number }
  | { type: 'limiter'; rpm: number }
  | { type: 'liftOff'; rpm: number; throttleBefore: number }
  | { type: 'backfire'; rpm: number };

export type DrivetrainEventListener = (e: DrivetrainEvent) => void;

/**
 * How long the revs take to absorb whatever the road's own rate does NOT explain — which, on a source
 * that reports whole km/h, is the step itself. Measured against a continuous source (staircase.test.ts):
 * at a normal pull the wobble in the rev climb drops from 3.7x its own size to 0.36x, and the revs sit
 * under 40 rpm behind the truth, a constant offset no ear can hear. Longer smooths further but starts
 * to push the automatic's change point up towards the cut.
 */
const ROAD_EASE_S = 0.30;
const STANDSTILL_KMH = 2;

export class VirtualDrivetrain {
  private readonly p: CarProfile;
  private mode: ShiftMode = 'auto';
  private driveStyle: DriveStyle = 'sport';
  private gear = 1;
  private rpm: number;
  private engineState: EngineState = 'on';
  private flareRpm = 0;
  private flareT = 0;        // seconds since the engine caught (start-up warm-up profile)
  private flareOn = false;
  private launch = false;
  pitLimiterOn = false;
  private postShift = 0;   // seconds of throttle recovery after an upshift (re-engagement)
  private launchPhase = 0;
  private launchT = 0;       // seconds since launch control was armed (two blips, then the climb)
  private static readonly LAUNCH_RAMP_S = 4;   // Marc: idle → 80 % → 60 % → 100 % of the launch revs, 4 s, super slow (timing as the original)
  /** launch rpm: the shift strip lit up to the LAST two (centre) reds — thr for LED 7 of 8 is 0.958·max, LED 8 at 0.974 */
  private static readonly LAUNCH_RPM_FRAC = 0.966;
  /** …unless the car says otherwise. A race car holds the line just under the cut; a road supercar's
   *  thrust mode holds a low, fixed number and lets the clutch do the rest (Aventador: 5000). */
  private launchHoldRpm(): number { return this.p.launch?.holdRpm ?? this.p.maxRpm * VirtualDrivetrain.LAUNCH_RPM_FRAC; }
  /** the launch RUN: the gears the box changes at the launch's own points before it goes back to normal */
  private launchRun = false;
  private upshiftRpmNow = 0;
  /** the shift strip is a sequence the automatic box OBEYS: it changes up only once the strip has filled,
   *  held, and blinked blue (Marc: "clavar el cambio con las luces siempre", whatever the gear ratio) */
  private readonly ladder = new ShiftLadder();
  private lights: LadderState = { stage: 0, blue: false, dark: false };
  private wantUp = false;
  private accelNow = 0;
  /** LAUNCH BY BRAKE (Marc): the brake held FULLY for 5 s at a standstill arms launch control by itself */
  private brakeHoldS = 0;
  private brakeArmable = true;
  private static readonly LAUNCH_BRAKE_HOLD_S = 5;
  private static readonly LAUNCH_BRAKE_FULL = 0.85;
  private static readonly LAUNCH_CARRY_FRAC = 0.72;   // getaway revs carried after release (unchanged feel)
  private static readonly CRANK_RPM = 280;
  private throttleIntent = 0;
  private throttleSmoothed = 0;
  private brake = 0;
  /** clutch state: opens at standstill (and when braking to a stop), slips through the pull-away at
   *  ~idle + pedal·30 % of the range, and locks once the wheels catch the engine (raw ≥ slip rpm). */
  private clutchLocked = true;
  /** throttle as the clutch foot feels it: rises quickly, DECAYS SLOWLY — pedal stabs (keyboard taps)
   *  must not crash the slipping revs to idle and reflare them ("parece que se vaya a calar") */
  private slipThr = 0;
  private launchCarry = 0;
  // launch GETAWAY (Marc): once the car actually moves off the line, the revs rise a touch with the
  // first wheelspin, BOG DOWN ~30 % as the clutch bites, then climb again and hand over to the
  // normal wheel-driven mapping by ~30 km/h. Shaped by SPEED, not time.
  private launchGetaway = false;
  private launchGetawayRpm = 0;
  private creepSlew = 0;   // glided rpm target through the standstill→rolling handover (Marc: the idle→first-km/h step)
  private launchDrop = 0;    // seconds of slowed rev fall after launch control is cancelled at standstill   // seconds of launch-rpm carry-over after launch control releases
  private standstillRev = 0; // seconds sitting at a dead stop with the pedal down (revving, not launching)
  private lastShiftDir = 0;  // +1 after an upshift, −1 after a downshift (hunting guard)
  private lowRevsFor = 0;    // seconds the revs have sat under the DRIVE downshift point (sustained, not a blip)
  private decelFor = 0;      // seconds the car has been slowing (a lift or the brake), for the early downshift
  private decelFrom = 0;     // speed when the slowing began: a real slow-down loses km/h, a wobble does not
  private decelDownAt = -10; // clock of the last decel downshift: the box does not climb back until the pedal asks
  private rpmJit = 0;        // a real engine never sits pinned: ±25 rpm life on the needle and the voice
  private slipTgt = 0;       // heavy, inertial slip target — pedal taps must not pump the revs (Marc: no surging)

  private shifting = false;
  private shiftDir: -1 | 0 | 1 = 0;
  private shiftElapsed = 0;
  private pendingGear = 1;
  private timeSinceShift = 10;
  private pendingManual: -1 | 0 | 1 = 0;

  private limiter = false;
  private limiterPhase = 0;

  private liftOffTimer = 0;
  private throttleHistory: { t: number; v: number }[] = [];
  private clock = 0;

  private lastSpeed = 0;
  private derivedAccel = 0;
  // A STAIRCASE IS NOT A SPEED, part two. v500 taught the GPS estimator to ramp through a source that
  // reports whole km/h; it could not help the sources that never go through it (CAN, the relay, the
  // app, a sim rig). And the staircase does not need a coarse source to show: in first gear one km/h
  // is ~79 rpm on this V12, the rpm target is the wheel speed exactly, and the engine's inertia limit
  // (thousands of rpm per second) is far too generous to stop 79 rpm landing inside ONE frame. So the
  // revs jumped a step and then sat flat until the next whole km/h — Marc, on his own sim at home:
  // 'a cada km/h de más que voy las rpm suben a trompicones'.
  // The cap below is the physics that was missing: with the clutch locked the crank is bolted to the
  // wheels, so the revs cannot climb faster than the ROAD is climbing. This is that acceleration,
  // averaged over about a second so a staircase's own derivative (zero, zero, zero, a whole km/h in
  // one frame) cannot spike it.
  private roadAccel = 0;
  private accelEst = 0;
  /** seconds left of the clutch biting after a dip: the revs cross from the slip target to the wheels */
  private biteS = 0;
  /** the road speed two seconds ago: 'is the car actually gaining' asked over a span no wander can fake */
  private speedTrail: { t: number; v: number }[] = [];
  /** the gear the rev cap was last applied in — a gearchange is a real step and must not be capped */
  private cappedInGear = 0;

  private readonly listeners = new Set<DrivetrainEventListener>();
  private state: DrivetrainState;

  constructor(profile: CarProfile) {
    this.p = profile;
    this.rpm = profile.idleRpm;
    this.state = this.snapshot(0, 0);
  }

  // ---- public API --------------------------------------------------------

  on(listener: DrivetrainEventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  getState(): DrivetrainState {
    return this.state;
  }

  getMode(): ShiftMode {
    return this.mode;
  }

  setMode(mode: ShiftMode): void {
    this.mode = mode;
  }

  setDriveStyle(style: DriveStyle): void {
    this.driveStyle = style;
  }

  getDriveStyle(): DriveStyle {
    return this.driveStyle;
  }

  toggleMode(): ShiftMode {
    this.setMode(this.mode === 'auto' ? 'manual' : 'auto');
    return this.mode;
  }

  /** Manual shift request (steering-wheel button, keyboard, touch). */
  requestShift(dir: -1 | 1): void {
    this.pendingManual = dir;
  }

  /** highest gear allowed (DEMO: 2nd) — null = all gears */
  gearCap: number | null = null;
  /**
   * THE CAR'S OWN GEAR, when we can see it. An OBD dongle gives speed and rpm, and their quotient says
   * which gear the driver has engaged (see GearLearner), so the box stops deciding for itself and simply
   * copies them: the sound changes gear at the instant they do, downshifts included, and holds a gear
   * for as long as they hold it. What it does NOT copy is their revs — inside the gear this engine still
   * stretches its own range with road speed, or a car cruising at 2000 rpm would keep it mumbling
   * (Marc's call: 'la B').
   */
  followGear: number | null = null;
  /** CAN: selector in P / R / N */
  neutral = false;
  selector: string | null = null;
  private topGear(): number { return this.gearCap ? Math.min(this.gearCap, this.p.gearTopSpeedsKmh.length) : this.p.gearTopSpeedsKmh.length; }

  /** Advance the model by dtS seconds with the latest telemetry. */
  tick(input: DrivetrainInput, dtS: number): DrivetrainState {
    const dt = Math.min(Math.max(dtS, 0.001), 0.1);
    this.clock += dt;
    this.timeSinceShift += dt;

    const speed = this.neutral ? 0 : Math.max(0, input.speedKmh);   // in P / R / N the engine is decoupled from the road
    // derived accel as fallback when source has none
    this.derivedAccel = 0.8 * this.derivedAccel + 0.2 * (((speed - this.lastSpeed) / 3.6) / dt);
    this.lastSpeed = speed;
    // …and no reading is allowed to claim more than a car can do: a whole km/h arriving between two
    // frames is 17 m/s² on paper, and even a half-second average swings a full g on that
    const accel = Math.max(-12, Math.min(12, input.accelMs2 ?? this.derivedAccel));
    this.accelNow = accel;
    this.roadAccel += (accel - this.roadAccel) * Math.min(1, dt / 0.5);
    // the throttle estimate's acceleration: calmer than the frame's, quicker than the road's - a lift
    // still reads within a quarter of a second, a whole-km/h step no longer reads as a stab
    this.accelEst += (accel - this.accelEst) * Math.min(1, dt / 0.25);
    this.speedTrail.push({ t: this.clock, v: speed });
    while (this.speedTrail.length > 2 && this.speedTrail[0].t < this.clock - 2.0) this.speedTrail.shift();

    // 1) throttle + brake intent
    // …and the brake, where there is no pedal on the wire, is read off the ROAD's acceleration: off
    // the frame's, one km/h of GPS wander was a full brake for a frame, and one frame is all the
    // gearbox needed to call it a lift into a corner (the 1-2-1 dance, from the Tesla, again)
    this.brake = input.brake ?? (this.roadAccel < -1 ? clamp01(-this.roadAccel / 7) : 0);
    const intent = input.pedal ?? this.estimateThrottle(speed, this.accelEst);
    this.throttleIntent = intent;
    // light smoothing so estimated throttle doesn't jitter
    this.throttleSmoothed += (intent - this.throttleSmoothed) * Math.min(1, dt / 0.06);
    this.slipThr += (this.throttleSmoothed - this.slipThr) * Math.min(1, dt / (this.throttleSmoothed > this.slipThr ? 0.25 : 1.3));
    this.pushThrottleHistory(this.throttleSmoothed);

    const stationary = speed < STANDSTILL_KMH;
    if (this.launch && speed > 4) {
      this.launch = false;
      this.launchCarry = 1.2;   // off the line → slip carries the launch revs down
      this.launchGetaway = true;
      // 'pull' holds the number the box was holding, bounce and all, as the floor the clutch slips at
      this.launchGetawayRpm = this.p.launch?.getaway === 'pull'
        ? this.launchHoldRpm()
        : Math.max(this.rpm, this.p.idleRpm * 2);
      this.launchRun = (this.p.launch?.shiftRpm?.length ?? 0) > 0;
    }
    this.launchCarry = Math.max(0, this.launchCarry - dt);
    // pedal down at a dead stop that is NOT going anywhere (parked / neutral / held): after a beat it is
    // someone revving the engine, not a getaway — free rev opens up
    this.standstillRev = stationary && this.throttleSmoothed > 0.05 ? this.standstillRev + dt : 0;
    // FREE REV IS "THE ENGINE IS NOT IN GEAR", and every dash reads it as "print N". Launch control is
    // the one standstill where that is false: the box has FIRST engaged and is holding the car on the
    // clutch, which is the whole mechanism (Marc: "en launch siempre tiene que ponerse la primera
    // marcha, ahora está en N"). The load state keeps its own test below, so nothing about the sound
    // moves - a launched car is still not idling.
    // …and only once it is PLAINLY a rev: a pedal at a dead stop reads as N after 0.6 s of going
    // nowhere, never on the pull-away itself (Marc: 'cuando paso de 0 a 1 pasa a N y luego vuelta a 1')
    const freeRev = stationary && speed < 0.3 && !this.launch && this.standstillRev > 1.0;
    // LAUNCH BY BRAKE (Marc): standing in D with the brake pressed to the floor for 5 s arms launch control
    // by itself. Once armed the brake can come off (the car's hold keeps it still) and the getaway releases
    // it as before. The brake has to be released before another hold can arm it again, and the selector
    // leaving D cancels it: a launch in P, R or N is nothing.
    if (this.engineState === 'on' && stationary && !this.neutral && !this.launch && this.brake >= VirtualDrivetrain.LAUNCH_BRAKE_FULL && this.brakeArmable) {
      this.brakeHoldS += dt;
      if (this.brakeHoldS >= VirtualDrivetrain.LAUNCH_BRAKE_HOLD_S) { this.brakeArmable = false; this.brakeHoldS = 0; this.setLaunch(true); }
    } else {
      this.brakeHoldS = 0;
    }
    if (this.brake < 0.5) this.brakeArmable = true;
    if (this.neutral && this.launch) this.setLaunch(false);
    if (this.launchRun && stationary) this.launchRun = false;   // stopped: whatever it was, the run is over

    // clutch: in at a standstill; dipped when braking down to a stop (a driver declutches before the
    // engine is dragged down — no near-stall shudder on every braked stop); locks once the wheels catch up
    const rawNow = this.rpmForGearRaw(speed, this.gear);
    const wasLocked = this.clutchLocked;
    if (stationary) { this.clutchLocked = false; if (this.slipTgt <= 0) this.slipTgt = this.p.idleRpm; }
    else if (this.shifting) this.clutchLocked = true; // a gearchange re-engages: the downshift blip must sound (auto-blipper)
    // THE DIP HAS HYSTERESIS. It used to open at brake > 0.15 and close the moment the brake read
    // 0.15 again, and a brake ESTIMATED off the road's deceleration sits exactly there while a car
    // rolls down to a stop: the clutch chattered, and every chatter swung the revs 190 rpm in one
    // frame between the slip target and the wheels (Marc, at a crawl: 'va a trompicones').
    else if (this.brake > (this.clutchLocked ? 0.25 : 0.10) && rawNow < this.p.idleRpm * 1.6) this.clutchLocked = false;
    // Marc: no pull-away slip phase — rolling = clutch locked (the slip hold + catch between ~10 and
    // ~26 km/h read as 'a clutch noise' he did not want); the launch carry keeps its slip briefly
    else if (!this.clutchLocked && (rawNow >= this.slipRpm() || this.launchCarry <= 0)) this.clutchLocked = true;
    // …and when it does bite again it bites over a few tenths, not between two frames
    if (this.clutchLocked && !wasLocked && !stationary) this.biteS = 0.35;
    this.biteS = Math.max(0, this.biteS - dt);

    // 2) gear selection
    if (!stationary) this.updateShifting(speed, dt);
    else this.cancelPendingManual();

    // 3) effective throttle (cuts / blips)
    let throttle = this.throttleSmoothed;
    if (this.postShift > 0) {
      this.postShift -= dt;
      throttle = Math.min(throttle, 0.3 + 0.7 * (1 - this.postShift / 0.16));
    }
    if (this.shifting) {
      const frac = this.shiftElapsed / (this.shiftDurS());
      // Soft torque cut, not a hard 0 — keeps the ON layer present so the overrun
      // sample doesn't slam in on every gearchange (that read as a TC-like stutter).
      if (this.shiftDir > 0) throttle = 0.05;   // ignition cut: the on-layer drops hard, overrun takes over
      else throttle = frac < 0.75 ? Math.max(throttle, this.p.shift.blipStrength) : 0.16; // blip
    }

    // 4) rpm target + inertia
    let target: number;
    if (this.engineState !== 'on') {
      // engine off: no combustion — revs fall to 0 (or sit at starter speed while cranking)
      throttle = 0;
      target = this.engineState === 'cranking' ? VirtualDrivetrain.CRANK_RPM : 0;
    } else if (stationary) {
      // held on the brake (or blipping in the pits): free rev — wide-open throttle reaches the limiter.
      // Rolling off without brake: the getaway slip rpm, so flooring it from a stop LAUNCHES at ~3.7k
      // instead of screaming to the limiter and collapsing once the car moves.
      target = this.brake > 0.15 || this.throttleSmoothed < 0.05 || this.standstillRev > 0.6
        ? this.p.idleRpm + this.throttleSmoothed * (this.p.maxRpm - this.p.idleRpm) * 1.05
        : this.slipRpm();
      if (this.flareOn) {
        // START-UP: the shape of the car's OWN start recording, because the recording and this curve
        // are crossfaded into each other while both are sounding — the engine catches high, falls onto
        // a fast idle, holds it, and only then eases down to tickover (CarProfile.start).
        // The defaults are the 911's Run_03 start, which catches at ~1.9×idle and falls STRAIGHT to a
        // lively idle (~1.15×): that car has no fast idle to speak of, a cold Ferrari has seconds of it.
        this.flareT += dt;
        const st = this.p.start;
        const fast = st?.fastIdleRpm ?? this.p.idleRpm * 1.15;
        const catchTau = st?.catchTauS ?? 0.18;
        const hold = st?.holdS ?? 2.4;
        const ease = st?.easeS ?? 0.9;
        let want: number;
        if (this.flareT < hold) want = fast + (this.flareRpm - fast) * Math.exp(-this.flareT / catchTau);
        else want = this.p.idleRpm + (fast - this.p.idleRpm) * Math.exp(-(this.flareT - hold) / ease);
        if (this.flareT > hold + ease * 4) this.flareOn = false;
        target = Math.max(target, want);
      }
      if (this.launch) {
        this.launchT += dt;
        if (this.launchT < VirtualDrivetrain.LAUNCH_RAMP_S) {
          // Marc: ONE slow sweep — idle → 80 % of the launch revs (1.6 s) → back to 60 % (1 s) → 100 % (1.4 s)
          const T = this.launchHoldRpm(), idle = this.p.idleRpm, t = this.launchT;
          const lerp = (a: number, b: number, k: number) => a + (b - a) * Math.max(0, Math.min(1, k));
          const frac = t < 1.6 ? lerp(0, 0.8, t / 1.6) : t < 2.6 ? lerp(0.8, 0.6, (t - 1.6) / 1) : lerp(0.6, 1, (t - 2.6) / 1.4);
          target = idle + (T - idle) * frac;
          throttle = Math.max(throttle, 0.2 + 0.7 * frac);
        } else {
          // launch control: hold the engine at the launch rpm with the characteristic 7 Hz bounce…
          // …unless the car says it does not have one. That chop is a race car's ignition cut and it
          // sounds like the rev limiter, because it IS the rev limiter's mechanism; a road car's thrust
          // mode holds the number without it (Marc, of the SVJ at 5000: "no puede hacer el tremble como
          // si estuviera en el rev limiter"). The throttle flutter goes with it — that, not the 60 rpm,
          // is what the ear hears, because it swings the mixer between the ON and OFF layers at 7 Hz.
          this.launchPhase += dt * 7;
          const amp = this.p.launch?.bounceRpm ?? 60;   // small enough to stay above the LED-7 threshold
          const down = amp > 0 && (this.launchPhase % 1) >= 0.5;
          target = this.launchHoldRpm() - (down ? amp : 0);
          throttle = Math.max(throttle, down ? 0.35 : 1);
        }
      }
    } else if (!this.clutchLocked) {
      // clutch dipped or slipping: a heavy, inertial target — taps swell it barely, sustained pedal builds
      // it. Creeping sounds like the Run_08 paddock: near-constant revs, zero pumping.
      const want = Math.max(this.rpmForGearRaw(speed, this.gear), this.slipRpm());
      if (this.slipTgt <= 0) this.slipTgt = this.p.idleRpm;
      this.slipTgt += (want - this.slipTgt) * Math.min(1, dt / (want > this.slipTgt ? 0.8 : 1.5));
      target = this.slipTgt;
    } else {
      // rolling in gear, clutch locked: the wheels set the revs — at a crawl the engine lugs below idle ('se ahoga') down to ~68 % of idle
      // Marc: the pull-away sound must start the moment the car rolls, not ~28 km/h later when the Porsche
      // 1st-gear curve finally climbs past idle — so the floor rises with the pedal (idle → +40 % at full
      // throttle): the revs leave idle as soon as you move and the wheel curve takes over smoothly (max)
      const floor = this.p.idleRpm * (1 + 0.4 * this.throttleSmoothed);
      const wheels = this.rpmForGearRaw(speed, this.gear);
      target = Math.max(floor, wheels);
      // the clutch biting: the slip target hands over to the wheels across the bite, not in a step
      if (this.biteS > 0 && this.slipTgt > 0) target += (this.slipTgt - target) * (this.biteS / 0.35);
    }
    if (this.launch && this.engineState === 'on' && !stationary) {
      // rolling off with the launch still armed (2→4 km/h): keep holding the launch revs — without this
      // the slip branch bled ~1.5k rpm through the gap and the getaway then held the already-fallen
      // value, which read as a sudden drop right off the line
      target = Math.max(target, this.launchHoldRpm() - 60);
      throttle = Math.max(throttle, 0.9);
    }
    if (this.launchGetaway && this.engineState === 'on' && this.p.launch?.getaway === 'pull') {
      // THE ROAD-CAR GETAWAY (Marc, from the Aventador: "se queda a 5000 rpm y luego estira hasta las
      // 8200"). Thrust mode holds a low number on the line and the clutch, not the engine, absorbs the
      // launch: the revs stay where the box put them while the car accelerates underneath, and the
      // moment the wheels have caught up with them the engine simply follows the gearbox to the cut.
      // …and it SAGS while it does it (Marc: "en el launch las rpm siempre tienen que bajar un poco").
      // A dead-flat plateau at the launch revs is the one thing a real launch never gives you: the
      // clutch takes the engine's torque the instant it is dumped and the engine bogs for it, four or
      // five hundred revs, before the car starts carrying it again. Shaped by SPEED, like the Cup's
      // curve above, so it is the same sag whether the launch is brutal or half-hearted.
      const L = this.launchGetawayRpm;
      const sag = speed < 14 ? 1 - 0.12 * Math.min(1, (speed - 4) / 10)   // 100 → 88 by 14 km/h: the bog
        : speed < 20 ? 0.88                                               // …held there, or the engine's
        : 0.88 + 0.12 * Math.min(1, (speed - 20) / 18);                   // own inertia rounds it away
      const floor = L * sag;
      const wheel = this.rpmForGearRaw(speed, this.gear);
      // it ends when the wheels have caught the engine — or the moment the driver stops launching. That
      // second test matters here and not in the curve below: this floor holds until the car is doing 60,
      // so a lift at 20 would otherwise keep the engine sitting at the launch revs on its own.
      if (speed < 1.5 || wheel >= floor || this.accelNow < -0.5 || this.throttleSmoothed < 0.3) this.launchGetaway = false;
      else {
        target = Math.max(target, floor);
        throttle = Math.max(throttle, 0.9);
      }
    } else if (this.launchGetaway && this.engineState === 'on') {
      if (speed >= 34 || speed < 1.5) this.launchGetaway = false;   // hooked into the sim — or the launch was aborted
      else {
        // ★ MARC-RATIFIED FINAL (2026-08-26, "FUCKING PERFECT") — do not retune these numbers. ★
        // Curve (static launch rpm = 100): a SLIGHT climb to 105, one descent to 90 (below the start),
        // and from there it picks up with the sim. launchShape.test.ts asserts this exact shape.
        const L = this.launchGetawayRpm, sK = speed;
        let shaped: number;
        if (sK < 15) shaped = L * (1 + 0.05 * Math.max(0, (sK - 4) / 11));          // 100 → 105 by 15 km/h, un pelín
        else if (sK < 24) shaped = L * (1.05 - 0.15 * ((sK - 15) / 9));             // 105 → 90, one clean descent below the start
        else shaped = L * 0.90;                                                      // riding at 90 into the hand-over
        shaped = Math.min(shaped, this.p.maxRpm * 0.99);                             // …and the climb stays JUST under the cut: no limiter chop mid-getaway
        const k = Math.max(0, Math.min(1, (sK - 24) / 10));                         // 24→34 km/h: gradual hand-over to the simulator
        target = Math.min(this.p.maxRpm, shaped * (1 - k) + target * k);
        throttle = Math.max(throttle, 0.85);
      }
    }
    // Marc: the idle (perfect) → first km/h transition was a STEP — the standing and rolling target
    // formulas differ, and the flip at 2 km/h was audible. Glide the target through 0.5–8 km/h so the
    // handover is a curve, not a cliff. Launch, gearchanges and free-revving stay immediate.
    if (this.engineState === 'on' && !this.launch && !this.launchGetaway && !this.shifting && speed > 0.5 && speed < 8) {
      if (this.creepSlew <= 0) this.creepSlew = this.rpm;
      this.creepSlew += (target - this.creepSlew) * Math.min(1, dt / 0.3);
      target = this.creepSlew;
    } else {
      this.creepSlew = target;
    }
    const rates = stationary
      ? [this.p.engine.freeRevUpRpmPerS, this.p.engine.freeRevDownRpmPerS]
      : !this.clutchLocked
        ? [2600, 2000]   // slipping: the revs swell gently with the foot — a pedal tap must not whip the voice
        : [this.p.engine.inertiaUpRpmPerS, this.p.engine.inertiaDownRpmPerS];
    if (this.engineState !== 'on') { rates[0] = 2500; rates[1] = 1800; } // starter spin-up / spin-down
    // launch control (Marc): the climb idle → launch revs and the fall back to idle are a touch slower
    this.launchDrop = Math.max(0, this.launchDrop - dt);
    let maxStep = (target > this.rpm ? rates[0] : rates[1]) * dt;
    // AT A CRAWL THE REVS MOVE AT THE PACE OF A FOOT. Under ~15 km/h in first the target is whichever
    // is higher of the pedal floor (idle to +40 %) and the wheels, and both were allowed to move at
    // the engine's full inertia rate: an estimated throttle going 0.27 -> 0 between two frames took
    // the revs 1110 -> 1000 in ONE frame, and the hand-over from floor to wheels did 191 in one.
    // A lurch either way (Marc, at low speed: 'va a trompicones el motor'). The whole crawl regime
    // is capped at 2500 rpm/s now - still brisk, a fifth of the inertia rate - and the cap does not
    // care which of the two is winning, because THAT flip was the second lurch.
    if (this.clutchLocked && !this.shifting && this.engineState === 'on' && rawNow < this.p.idleRpm * 1.6) maxStep = Math.min(maxStep, 2500 * dt);
    // IN GEAR THE REVS FOLLOW THE ROAD, and the road cannot jump. Not while shifting (a gearchange IS a
    // step), not at a standstill, not in a launch or on the limiter, and not on the tick a gear changed.
    const roadBound = this.engineState === 'on' && this.clutchLocked && !this.shifting && !stationary
      && !this.launch && !this.launchGetaway && this.launchCarry <= 0 && !this.limiter
      && this.gear === this.cappedInGear
      // …and not for the half second after a gearchange: the crank is still catching the new ratio,
      // thousands of rpm at the inertia rate, and the last few hundred of that landing are NOT a
      // quantiser's leftovers to be eased (the Aventador's launch landed in 2nd a hundred rpm late)
      && this.timeSinceShift > 0.5
      // …and only while the ROAD is what is turning the crank. Crawling in a high gear the revs sit on
      // their idle floor and the wheels have nothing to say about them; feeding the road's rate in there
      // dragged a decelerating car below idle.
      && rawNow > this.p.idleRpm * 1.05;
    this.cappedInGear = this.gear;
    const delta = target - this.rpm;
    if (stationary && this.launch && this.launchT < VirtualDrivetrain.LAUNCH_RAMP_S) {
      // the launch sweep follows its own slow trajectory — but never faster than 6k rpm/s, so arming
      // from high revs settles onto the curve instead of snapping to it (Marc: no abrupt transitions)
      const step = delta * Math.min(1, dt / 0.12), cap = 6000 * dt;
      this.rpm += Math.abs(step) > cap ? Math.sign(step) * cap : step;
    } else if (stationary && this.launchDrop > 0 && delta < 0) {
      // launch cancelled at standstill (Marc): a SMOOTH exponential settle back to idle, not a linear fall
      this.rpm += delta * Math.min(1, dt / 0.55);
    } else {
      // Two terms, and the split is the whole point. The FEED-FORWARD turns the crank at exactly the
      // rate the road is turning the wheels, so a real acceleration is followed with no lag at all;
      // the correction then closes whatever is left over gently, which is where a staircase lives. Ease
      // the WHOLE delta instead and the revs sit hundreds low under hard acceleration; skip the ease
      // and every whole km/h lands in one frame, which is the trompicón.
      let want = delta;
      if (roadBound) {
        const perKmh = this.rpmForGearRaw(1, this.gear);          // revs this gear turns per km/h
        const road = this.roadAccel * 3.6 * perKmh * dt;          // what the road alone turns the crank
        const rest = delta - road;
        // Only what a QUANTISER could have caused is eased. One whole km/h of revs is the yardstick:
        // inside it the leftover is almost certainly a step in a coarse speed and is absorbed slowly;
        // well outside it (a gearchange landing, a launch releasing, the box picking a gear) the revs
        // move as they always did. Without this the ease also smeared the things that are REALLY steps.
        // six km/h of revs, not one: the car's browser rounds to whole km/h AND wanders up to three
        // either way at a crawl (Marc: 'cambios suaves de 1 km/h arriba y abajo, o dos o tres'), so
        // two consecutive readings can be six apart, and that is still the quantiser, not the car.
        // A gearchange landing or a launch releasing is thousands of rpm away from this.
        const q = perKmh * 6;
        const hard = clamp01((Math.abs(rest) - q) / (2 * q));
        const ease = Math.min(1, dt / ROAD_EASE_S);
        want = road + rest * (ease + (1 - ease) * hard);
      }
      this.rpm += Math.abs(want) <= maxStep ? want : Math.sign(want) * maxStep;
    }
    if (this.shifting && this.shiftDir > 0) {
      // torque cut lets revs sag a little
      this.rpm -= 900 * dt;
    }

    // 5) limiter — but NOT during a shift: the instant you upshift you've left the
    // rev limiter, so the 14 Hz chop must not overlap the gearchange.
    const wasLimiter = this.limiter;
    // pit-lane limiter: the ECU chops at the capped speed exactly like the rev limiter, at whatever rpm
    // the gear puts you (Run_08: pinned at 60 km/h in 1st ≈ 5.8k) — same chop, same burst clip
    const pinned = this.limiter && target >= this.p.maxRpm - 30 && this.rpm >= this.p.maxRpm - 260;
    if (this.engineState === 'on' && this.pitLimiterOn && !this.shifting && speed > STANDSTILL_KMH) {
      this.limiter = true;
      this.limiterPhase += dt * 14;
      const chopOn = (this.limiterPhase % 1) >= 0.5;
      this.rpm = Math.max(this.p.idleRpm, this.rpm - (chopOn ? 140 : 0) * dt * 30);
      throttle = chopOn ? Math.min(throttle, 0.25) : throttle;
    } else if (this.engineState === 'on' && !this.shifting && (this.rpm >= this.p.maxRpm || pinned)) {
      this.limiter = true;
      this.limiterPhase += dt * 14; // ~14 Hz cut pattern
      const bounce = (this.limiterPhase % 1) < 0.5 ? 0 : 180;
      this.rpm = this.p.maxRpm - bounce;
      throttle *= (this.limiterPhase % 1) < 0.5 ? 1 : 0.2;
    } else {
      this.limiter = false;
    }
    if (this.limiter && !wasLimiter) this.emit({ type: 'limiter', rpm: this.rpm });
    // in gear at crawling speed the engine LUGS below idle ('se ahoga') instead of being held at idle
    const floor = this.engineState === 'on' ? (stationary || !this.clutchLocked ? this.p.idleRpm * 0.92 : Math.min(this.p.idleRpm * 0.92, Math.max(this.p.idleRpm * 0.68, this.rpmForGearRaw(speed, this.gear)))) : 0;
    this.rpm = Math.max(floor, this.rpm);
    // ±25 rpm of life for the NEEDLE only (Marc: 'nunca un motor está a 1700 clavao') — the voice keeps
    // the clean rpm: wobbling the audio pitch ±1.5 % was a warble
    if (this.engineState === 'on') this.rpmJit = Math.max(-25, Math.min(25, this.rpmJit * 0.88 + (Math.random() - 0.5) * 16));
    else this.rpmJit = 0;

    // 6) lift-off detection
    this.liftOffTimer = Math.max(0, this.liftOffTimer - dt);
    const liftOff = this.detectLiftOff();
    if (liftOff !== null) {
      this.liftOffTimer = 0.6;
      this.emit({ type: 'liftOff', rpm: this.rpm, throttleBefore: liftOff });
      if (this.rpm > this.p.maxRpm * 0.55 && Math.random() < 0.45) {
        this.emit({ type: 'backfire', rpm: this.rpm });
      }
    }

    // 7) the shift strip, sequenced after the revs of this tick; the next tick's gear decision reads it
    // THE STRIP IS A REV STRIP, anchored to the RACE point (the blue lights just under the cut) in EVERY
    // mode, like the real dash. DRIVE changes at calm revs and the strip simply shows where they are (three
    // greens and a yellow a side, say); scaling it to the DRIVE point made it flash blue at 80 % (Marc:
    // 'las luces me marcan flashing blue, que es signo de que están al límite').
    this.lights = this.ladder.update({
      dt,
      engineOn: this.engineState === 'on',
      rpm: this.rpm,
      idleRpm: this.p.idleRpm,
      refRpm: this.lightsRef(),
      gear: this.gear,
      gearTops: this.p.gearTopSpeedsKmh,
      shifting: this.shifting,
      shiftDir: this.shiftDir,
      atCut: this.limiter && !this.pitLimiterOn && this.rpm >= this.p.maxRpm - 300,
      launch: this.launch,
      holdGear: this.launch || this.launchGetaway,   // the box is holding 1st: no blue, it would promise a change
      wantUp: this.wantUp && this.driveStyle === 'sport',   // only RACE waits for the show; DRIVE just changes
      raceShow: this.mode === 'auto' && this.driveStyle === 'sport',
    });

    this.state = this.snapshot(speed, accel, throttle, freeRev);
    return this.state;
  }

  // ---- internals ---------------------------------------------------------

  /** Ignition state (ENG START / STOP). Switching on from 'off' passes through the caller's cranking phase. */
  setEngineState(st: EngineState, flareRpm = 0): void {
    if (st === this.engineState) return;
    this.engineState = st;
    if (st === 'off') { this.limiter = false; this.flareOn = false; this.launch = false; }
    if (st === 'on' && flareRpm > this.p.idleRpm) {
      this.flareRpm = flareRpm;
      this.flareT = 0;
      this.flareOn = true;
      this.rpm = Math.max(this.rpm, this.p.idleRpm * 0.9); // catches right away
    }
  }

  /** Launch control: only arms standing still with the engine running; releases itself once rolling. */
  setLaunch(on: boolean): boolean {
    if (on && (this.engineState !== 'on' || this.state.speedKmh > 2)) return false;
    if (!on && this.launch && this.state.speedKmh <= 2) this.launchDrop = 2.5;   // cancelled standing still → slow fall to idle
    this.launch = on;
    this.launchPhase = 0;
    this.launchT = 0;
    if (on) {
      // armed while already revving: enter the sweep where its curve MEETS the current rpm — starting
      // from t=0 (idle) would command a cliff-sized drop on the first tick (Marc: no abrupt transitions)
      const T = this.p.maxRpm * VirtualDrivetrain.LAUNCH_RPM_FRAC;
      const fracNow = Math.max(0, Math.min(0.8, (this.rpm - this.p.idleRpm) / Math.max(1, T - this.p.idleRpm)));
      this.launchT = 1.6 * (fracNow / 0.8);
    }
    return on;
  }

  isLaunch(): boolean {
    return this.launch;
  }

  getEngineState(): EngineState {
    return this.engineState;
  }

  rpmForGear(speedKmh: number, gear: number): number {
    const top = this.p.gearTopSpeedsKmh[gear - 1];
    const raw = (speedKmh / top) * this.p.maxRpm;
    return Math.max(this.p.idleRpm, raw);
  }

  /** where the blue lights sit: the RACE change point, 95.5 % of the cut for a 'kiss the limiter' profile */
  private lightsRef(): number {
    // an explicit change point is the change point: the lights have to fill toward where the box
    // really changes, or they complete after the shift has already happened
    return this.p.shift.upshiftAtRpm ?? Math.min(this.p.shift.autoUpshiftRpm, this.p.maxRpm * 0.955);
  }

  /** a downshift takes longer than a sequential upshift: clutch + blip + re-engage (~220 ms) */
  private shiftDurS(): number {
    return this.shiftDir < 0 ? 0.22 : this.p.shift.shiftTimeMs / 1000;
  }

  /** rpm the driver slips the clutch at: idle with the pedal up, up to ~30 % of the range flooring it
   *  (≈3.7k — a proper race-car getaway), plus the launch-control carry-over right after launch releases */
  private slipRpm(): number {
    // the driver's foot breathes — but ONLY in 2nd gear under 45 km/h (Marc's call); everywhere else the
    // slip target stays steady
    const breathe = this.gear === 2 && this.lastSpeed < 45 ? 1 + 0.025 * Math.sin(this.clock * 2.5) * Math.min(1, this.slipThr * 4 + 0.3) : 1;
    // with the pedal in, a real driver slips at ~1.8k+, not just above idle — and that is where the
    // steady paddock takes live (1.45–1.75k is a material hole that loops/warbles)
    // ANY feather of pedal slips at ~1.8k (super-slow creep lived in the 1.45–1.75k material hole)
    const floor = this.p.idleRpm * 1.15 * clamp01((this.slipThr - 0.015) / 0.03);   // getaway ~1.95k with the 1700 idle
    let r = Math.max(floor, this.p.idleRpm + this.slipThr * 0.3 * (this.p.maxRpm - this.p.idleRpm)) * breathe;
    if (this.launchCarry > 0) r = Math.max(r, this.p.maxRpm * VirtualDrivetrain.LAUNCH_CARRY_FRAC - (1.2 - this.launchCarry) * 2200);
    return r;
  }

  private rpmForGearRaw(speedKmh: number, gear: number): number {
    return (speedKmh / this.p.gearTopSpeedsKmh[gear - 1]) * this.p.maxRpm;
  }

  private estimateThrottle(speedKmh: number, accel: number): number {
    if (speedKmh < STANDSTILL_KMH) return 0;
    if (accel < -0.6) return 0;
    // accelerating: map ~0..4 m/s² to 0.25..1
    const accelTerm = accel > 0.15 ? 0.25 + clamp01(accel / 4) * 0.75 : 0;
    // cruising needs some throttle to hold speed (drag grows with speed)
    const cruise = 0.12 + 0.3 * clamp01(speedKmh / 200);
    return clamp01(Math.max(accelTerm, accel > -0.6 ? cruise : 0));
  }

  private updateShifting(speed: number, dt: number): void {
    if (this.shifting) {
      this.shiftElapsed += dt;
      if (this.shiftElapsed >= this.shiftDurS()) {
        if (this.shiftDir > 0) this.postShift = 0.16;   // torque re-engagement: throttle comes back over ~160 ms
        this.shifting = false;
        this.shiftDir = 0;
        this.gear = this.pendingGear;
      }
      return;
    }
    // LAUNCH HOLDS THE GEAR. The getaway shapes the revs near the cut on purpose, which is exactly where
    // the box wants to change up: it went to 2nd at walking pace and straight back to 1st (Marc saw the
    // gears "saltar super rápido" in the app and a 1-2-1 dance on the web). Nothing changes gear until the
    // launch has handed over to the ordinary wheel-driven mapping.
    if (this.launch || this.launchGetaway) {
      this.wantUp = false;
      this.pendingManual = 0;
      this.lowRevsFor = 0;
      this.decelFor = 0;
      return;
    }
    const gears = this.topGear();   // DEMO caps at 2nd
    // A DOWNSHIFT IS HEARD ONE AT A TIME. Under a hard brake every lower gear fits at once and the box
    // used to take them 250 ms apart, four blips smeared into one (Marc: 'cambia muy rapido y apenas
    // se escucha un buen regimen de reduccion'). After a downshift the next one waits 0.55 s: the
    // blip, the landing, then the next.
    const canShift = this.timeSinceShift >= Math.max(this.p.shift.minShiftIntervalMs / 1000, this.lastShiftDir < 0 ? 0.55 : 0);

    // THE LAUNCH RUN ends when its gears are used up, or when the driver is plainly no longer launching.
    // SLOWING is the test, not the throttle: with GPS the pedal is inferred from acceleration, and the
    // torque cut of the launch's own gearchange would read as a lift.
    if (this.launchRun && (this.gear > (this.p.launch?.shiftRpm?.length ?? 0) || this.accelNow < -0.5)) this.launchRun = false;

    // the driver's own box is visible: copy it and decide nothing
    if (this.followGear != null) {
      const want = Math.max(1, Math.min(gears, Math.round(this.followGear)));
      this.pendingManual = 0;
      this.wantUp = false;
      this.lowRevsFor = 0;
      this.decelFor = 0;
      this.upshiftRpmNow = this.p.shift.autoUpshiftRpm;   // the strip still fills toward the cut
      if (want !== this.gear && canShift) this.beginShift(want);
      return;
    }

    if (this.mode === 'manual') {
      const req = this.pendingManual;
      this.pendingManual = 0;
      this.wantUp = false;
      this.upshiftRpmNow = this.p.shift.autoUpshiftRpm;   // the strip fills toward the cut; the driver does the changing
      // no anti-stall downshift: a sequential box does not shift for you — in a tall gear at a crawl the
      // engine lugs below idle ('se ahoga'), which is exactly what Marc wants to hear (2nd at walking pace)
      if (req === 0 || !canShift) return;
      const next = this.gear + req;
      if (next < 1 || next > gears) return;
      this.beginShift(next);
      return;
    }

    // auto — shift points depend on the drive style
    this.pendingManual = 0;
    if (!canShift) return;

    // …unless this is a LAUNCH. A launched car does not change gear where the same car changes gear
    // cruising: the box has its own points for the first gears off the line and hands the car back
    // afterwards (Marc, the Aventador: 1st to 8200, 2nd to 8500, "y luego ya normal"). It goes straight
    // through — no throttle estimate, no waiting for the light strip: a launch change is programmed.
    const launchUp = this.launchRun ? this.p.launch?.shiftRpm?.[this.gear - 1] : undefined;
    if (launchUp != null) {
      this.upshiftRpmNow = launchUp;   // …and the strip fills to the point the box will really use
      this.wantUp = this.rpm >= launchUp && this.gear < gears;
      if (this.wantUp) this.beginShift(this.gear + 1);
      return;                          // nothing else decides anything while the launch is running
    }

    const t = this.throttleSmoothed;
    const sporty = this.driveStyle === 'sport';
    // DRIVE stretches each gear up the range without ever sounding like RACE; SPORT runs to the blue.
    // RACE changes JUST UNDER the cut, never on it (Marc: 'NUNCA debería en AUTO cambiar llegando al
    // limitador'): a profile that says 'kiss the limiter' gets its point pulled to 95.5 % of it, which is
    // where the blue lights sit on a real dash, and leaves the show 4.5 % of headroom before the chop
    const kiss = this.p.maxRpm * 0.955;
    // DRIVE sits clearly UNDER race (Marc, once RACE moved to 95.5 %: 'en DRIVE es prácticamente lo mismo
    // que en RACE'): light throttle ~62 %, full throttle ~82 % of the limiter. It still stretches each
    // gear, it just never sounds like the race box.
    const fullUp = sporty
      ? (this.p.shift.upshiftAtRpm ?? Math.min(this.p.shift.autoUpshiftRpm, kiss))
      : this.p.maxRpm * 0.82;
    const partUp = sporty ? Math.min(this.p.shift.partThrottleUpshiftRpm ?? this.p.maxRpm * 0.55, fullUp) : this.p.maxRpm * 0.62;
    // CORSA CHANGES AT THE LIMIT, WHATEVER THE FOOT IS DOING (Marc: 'tiene que cambiar al limite si va
    // en corsa'). The part-throttle point is DRIVE's; a race box holds the gear to the lights.
    const upAt = sporty ? fullUp : lerp(partUp, fullUp, smoothstep(t));
    this.upshiftRpmNow = upAt;   // published so the dash can scale its shift lights to the REAL shift point
    // HUNTING GUARD (DRIVE): right after a downshift the box must not climb straight back up unless the
    // revs are really at the top; right after an upshift it must not drop straight back down unless the
    // driver brakes or the engine is genuinely lugging. Around a boundary speed this used to ping-pong 2-3-2.
    const afterDown = this.lastShiftDir < 0 && this.timeSinceShift < 2.5;
    // …and after a downshift taken because the car was slowing, the box climbs back only when the pedal
    // really asks (or the revs are at the top): cruising through a slight wobble must not ping-pong
    const afterDecelDown = this.clock - this.decelDownAt < 4 && t < 0.4;
    const upGate = sporty ? true : !(afterDown && this.rpm < fullUp * 0.97) && !(afterDecelDown && this.rpm < fullUp * 0.97);
    // pinned at (or near) the cut the box shifts NO MATTER what the throttle estimate says: with GPS/RaceBox
    // the throttle is inferred from acceleration, and at constant speed it reads ~0 — Marc sat ON the
    // limiter in 1st, sound at the cut, and the box never shifted because of the t > 0.05 gate
    const pinned = this.rpm >= fullUp;
    // THE BOX WAITS FOR THE LIGHTS (Marc): wanting the change is one thing, the strip has to have filled,
    // held and blinked blue first. Under a steady climb the strip runs ahead by that much and the change
    // lands where it always did; when the throttle estimate moves the point under it, the box holds the
    // gear for the few tenths the show still needs rather than changing with the strip half lit.
    // RACE changes AT THE BLUE, not at the cut: the RACE point is the limiter itself, so a box that only
    // changed once the revs got there always chopped at least once, and any lateness in the lights turned
    // into a chop Marc could hear (2nd to 4th). Once the show is done and the revs are within 2 % of the
    // cut, the change goes now, the way a driver shifts on the lights rather than on the limiter.
    const atLights = sporty && this.ladder.ready && this.rpm >= fullUp - this.p.maxRpm * 0.01;
    this.wantUp = this.gear < gears && (this.rpm >= upAt || atLights) && (t > 0.05 || pinned || atLights) && (sporty || pinned || this.timeSinceShift > 1.0) && (upGate || pinned);
    if (this.wantUp) {
      // the hard stop: whatever the lights are doing, the box never lets the revs reach the cut in AUTO.
      // DRIVE does not wait for the strip at all: its point is far under the lights
      const hardUp = this.rpm >= this.p.maxRpm * 0.985;
      if (!sporty || this.ladder.ready || hardUp) this.beginShift(this.gear + 1);
      return;
    }
    if (this.gear > 1) {
      const lowerRpm = this.rpmForGear(speed, this.gear - 1);
      // kickdown: only when the engine is genuinely low for the throttle asked, the lower
      // gear lands clearly under the upshift point (no instant re-upshift), and never right
      // after a gearchange (DRIVE used to upshift at 72 %, land at ~50 % and kick down → ping-pong)
      const settled = this.timeSinceShift > (sporty ? 0.6 : 1.5);
      const kickdown = t > 0.85 && settled && lowerRpm < upAt - this.p.maxRpm * 0.1 && this.rpm < upAt * 0.62;
      // RACE: grab the lower gear the MOMENT it fits at the amber lights (Marc: 'que la marcha de abajo
      // saliera el limitador ámbar mínimo') — on any lift/brake, not only hard braking. Slowing is read
      // from the car itself too: with GPS the throttle is estimated and a gentle lift reads as cruise
      // THE ROAD'S ACCELERATION, NOT THE FRAME'S. A car that reports whole km/h hands over a train
      // of spikes - zero, zero, +16 m/s², zero, -16 - and every guard here that read `accelNow` saw
      // 'braking' and 'slowing' several times a second at a steady crawl, which is how the 1-2-1
      // dance came back from the Tesla after v508 had killed it on the sim (Marc: 'lo puto mismo').
      const decel = (this.roadAccel < -0.4 && speed - this.speedTrail[0].v < -1.5) || this.brake > 0.15;
      // A LIGHT PEDAL IS NOT A LIFT. `t < 0.3` alone called a gentle steady pull 'slowing', so in CORSA
      // the box changed up on the part-throttle point and grabbed the lower gear back a quarter of a
      // second later, over and over (Marc, first gear, accelerating very gently: 'te cambia a segunda
      // antes de llegar al rpm limiter, y luego inmediatamente te cambia de nuevo a 1a'). The car has to
      // actually be losing speed, or be coasting, for the lower gear to come back.
      // …and 'not gaining' is asked over two seconds of road, because a half-second average of a
      // staircase still swings from nothing to twice the truth between one whole km/h and the next
      const slowing = decel || (t < 0.3 && speed - this.speedTrail[0].v < 1.0);
      // …and nothing undoes a gearchange that has just been made unless the car really is slowing:
      // whatever the windows say, a box that changes up and straight back down is broken, not sporty.
      const justChanged = this.timeSinceShift < 1.2 && !decel;
      const raceDown = sporty && slowing && !justChanged && lowerRpm <= this.p.maxRpm * 0.94;
      // DRIVE, slowing down: the lower gear comes as soon as it lands under 80 % (Marc: 'en la
      // desaceleración que reduzca antes'), instead of waiting for the revs to sag under 60 % for 0.7 s.
      // The hunting guard after a downshift still keeps the box from climbing straight back
      if (!decel) this.decelFrom = speed;
      this.decelFor = decel ? this.decelFor + dt : 0;
      // a real slow-down, not a wobble: the car has lost 9 km/h since it started slowing, or the brake is on
      const reallySlowing = this.decelFor > 0.4 && (this.decelFrom - speed >= 9 || this.brake > 0.3);
      const driveDecelDown = !sporty && reallySlowing && lowerRpm <= this.p.maxRpm * 0.8 && this.timeSinceShift > 0.6;
      // DRIVE: downshift when revs sag under ~60 % of the limiter (Marc, third time: 'muchísimo antes') …
      const downAt = sporty ? this.p.shift.autoDownshiftRpm : this.p.maxRpm * 0.6;
      // … and the lower gear must land in the green (≤ 62 % of the limiter), never near the cut
      // … and in DRIVE the lower gear must sit clearly UNDER the light-throttle upshift point, otherwise the
      // two windows overlap and the box hunts (2nd 72 % ↔ 3rd 60 % used to overlap between 95 and 102 km/h)
      const landsCalm = sporty || lowerRpm <= Math.min(this.p.maxRpm * 0.8, partUp - this.p.maxRpm * 0.06);
      // sustained low revs (0.7 s), not a momentary dip; and not straight after an upshift unless braking / lugging
      this.lowRevsFor = this.rpm < downAt ? this.lowRevsFor + dt : 0;
      const afterUp = this.lastShiftDir > 0 && this.timeSinceShift < 3.0;
      const lugging = this.rpm < this.p.idleRpm * 1.45;
      const driveDown = !sporty && this.lowRevsFor > 0.7 && landsCalm && (!afterUp || this.brake > 0.3 || lugging);
      const sportDown = sporty && this.rpm < downAt && landsCalm && !justChanged;
      if (driveDown || sportDown || kickdown || raceDown || driveDecelDown) {
        if (driveDecelDown && !driveDown && !kickdown) this.decelDownAt = this.clock;
        this.beginShift(this.gear - 1);
      }
    }
  }

  private beginShift(toGear: number): void {
    const from = this.gear;
    this.shifting = true;
    this.shiftDir = toGear > from ? 1 : -1;
    this.lastShiftDir = this.shiftDir;
    this.lowRevsFor = 0;
    this.shiftElapsed = 0;
    this.pendingGear = toGear;
    this.timeSinceShift = 0;
    // suppress lift-off/backfire around the shift (the torque cut is not a lift-off)
    this.liftOffTimer = Math.max(this.liftOffTimer, 0.45);
    this.emit(
      toGear > from
        ? { type: 'shiftUp', fromGear: from, toGear, rpm: this.rpm }
        : { type: 'shiftDown', fromGear: from, toGear, rpm: this.rpm },
    );
  }

  private cancelPendingManual(): void {
    this.pendingManual = 0;
    this.wantUp = false;
    if (this.gear !== 1 && !this.shifting) this.gear = 1;
  }

  private pushThrottleHistory(v: number): void {
    this.throttleHistory.push({ t: this.clock, v });
    while (this.throttleHistory.length > 2 && this.throttleHistory[0].t < this.clock - 0.35) {
      this.throttleHistory.shift();
    }
  }

  /** Returns throttle-before value when a lift-off just happened, else null. */
  private detectLiftOff(): number | null {
    if (this.liftOffTimer > 0) return null;
    const now = this.throttleSmoothed;
    if (now > 0.15) return null;
    let peak = 0;
    for (const h of this.throttleHistory) if (h.t >= this.clock - 0.25 && h.v > peak) peak = h.v;
    if (peak - now >= 0.4 && this.rpm > this.p.idleRpm * 1.5) return peak;
    return null;
  }

  private snapshot(speed: number, accel: number, throttle = 0, freeRev = false): DrivetrainState {
    const rpmNorm = clamp01((this.rpm - this.p.idleRpm) / (this.p.maxRpm - this.p.idleRpm));
    let load: LoadState;
    if (this.limiter) load = 'limiter';
    else if (this.shifting) load = 'shift';
    else if (speed < STANDSTILL_KMH && !freeRev && !this.launch) load = 'idle';
    else if (this.brake > 0.15) load = 'brake';
    else if (throttle < 0.08) load = 'coast';
    else if (Math.abs(accel) < 0.6 && throttle < 0.5) load = 'cruise';
    else load = 'accel';

    return {
      engine: this.engineState,
      launch: this.launch,
      neutral: this.neutral,
      selector: this.selector,
      pitLimiter: this.pitLimiterOn,
      rpm: this.rpm,
      rpmDisplay: Math.round(this.rpm + this.rpmJit),
      rpmNorm,
      gear: this.gear,
      throttle,
      throttleIntent: this.throttleIntent,
      brake: this.brake,
      load,
      limiter: this.limiter,
      shifting: this.shifting,
      shiftDir: this.shiftDir,
      liftOff: this.liftOffTimer > 0,
      freeRev,
      clutchSlip: !this.clutchLocked,
      speedKmh: speed,
      accelMs2: accel,
      topSpeedKmh: this.p.gearTopSpeedsKmh[this.p.gearTopSpeedsKmh.length - 1],
      upshiftRpm: this.upshiftRpmNow,
      shiftLights: this.lights,
      mode: this.mode,
    };
  }

  private emit(e: DrivetrainEvent): void {
    this.listeners.forEach((l) => l(e));
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}
function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function smoothstep(t: number): number {
  const x = clamp01(t);
  return x * x * (3 - 2 * x);
}
